import { useState, useEffect, useCallback } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Search } from 'lucide-react'
import { MainNav } from './MainNav'
import { UserMenu } from './UserMenu'
import { SearchInput } from './SearchInput'
import type { Suggestion } from './SearchInput'
import { useDebounce } from '../../hooks'
import { supabase } from '../../lib/supabase'

export interface NavigationItem {
  label: string
  href: string
  isActive?: boolean
}

export interface User {
  id?: string
  name: string
  avatarUrl?: string
}

export interface AppShellProps {
  children: ReactNode
  navigationItems: NavigationItem[]
  user?: User
  onNavigate?: (href: string) => void
  onLogout?: () => void
  onSearch?: (query: string) => void
  onSubmitWord?: () => void
  onSignIn?: () => void
}

export function AppShell({
  children,
  navigationItems,
  user,
  onNavigate,
  onLogout,
  onSearch,
  onSubmitWord,
  onSignIn,
}: AppShellProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  useEffect(() => {
    async function fetchSuggestions() {
      if (debouncedSearchQuery.trim().length < 2) {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      try {
        const query = debouncedSearchQuery.toLowerCase()
        const { data, error } = await supabase
          .from('foreign_words')
          .select('word, word_arabic')
          .or(`word.ilike.%${query}%,word_arabic.ilike.%${query}%`)
          .limit(5)

        if (error) throw error

        const mappedSuggestions = (data || []).map((item) => ({
          word: item.word,
          wordArabic: item.word_arabic,
        }))

        setSuggestions(mappedSuggestions)
        setShowSuggestions(mappedSuggestions.length > 0)
      } catch (error) {
        console.error('Error fetching suggestions:', error)
        setSuggestions([])
        setShowSuggestions(false)
      }
    }

    fetchSuggestions()
  }, [debouncedSearchQuery])

  useEffect(() => {
    if (debouncedSearchQuery.trim()) {
      onSearch?.(debouncedSearchQuery)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
      onSearch?.('')
    }
  }, [debouncedSearchQuery, onSearch])

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    setShowSuggestions(false)
    if (searchQuery.trim()) {
      onSearch?.(searchQuery)
    } else {
      setSuggestions([])
      onSearch?.('')
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setShowSuggestions(true)
  }

  const handleSelectSuggestion = useCallback(
    (word: string) => {
      setSearchQuery(word)
      setShowSuggestions(false)
      onSearch?.(word)
    },
    [onSearch],
  )

  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestions(false)
  }, [])

  const toggleMobileSearch = () => {
    setIsSearchExpanded(!isSearchExpanded)
  }

  return (
    <div className='min-h-screen bg-slate-900 text-slate-100'>
      <Header>
        <HeaderContent>
          <MainNav items={navigationItems} onNavigate={onNavigate} />
          <Logo onNavigate={onNavigate} />
          <DesktopSearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onSubmit={handleSearch}
            suggestions={suggestions}
            showSuggestions={showSuggestions}
            onSelectSuggestion={handleSelectSuggestion}
            onCloseSuggestions={handleCloseSuggestions}
          />
          <MobileSearchToggle onClick={toggleMobileSearch} />
          <PrimaryActionButton onClick={onSubmitWord} />
          <AuthSection user={user} onSignIn={onSignIn} onLogout={onLogout} />
        </HeaderContent>
        <MobileSearchBar
          isExpanded={isSearchExpanded}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSubmit={handleSearch}
          suggestions={suggestions}
          showSuggestions={showSuggestions}
          onSelectSuggestion={handleSelectSuggestion}
          onCloseSuggestions={handleCloseSuggestions}
        />
      </Header>
      <MainContent>{children}</MainContent>
    </div>
  )
}

function Header({ children }: { children: ReactNode }) {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 border-b bg-slate-900 border-slate-800'>
      {children}
    </header>
  )
}

function HeaderContent({ children }: { children: ReactNode }) {
  return (
    <div className='flex items-center h-16 gap-4 px-4 mx-auto max-w-screen-2xl'>
      {children}
    </div>
  )
}

interface LogoProps {
  onNavigate?: (href: string) => void
}

function Logo({ onNavigate }: LogoProps) {
  return (
    <div className='flex items-center gap-2'>
      <button
        onClick={() => onNavigate?.('/')}
        className='text-xl font-bold transition-all duration-300 text-amber-400 hover:text-amber-500'>
        كلامنتينا
      </button>
      <img src='/logo.svg' alt='كلامنتينا Logo' className='w-8 h-8' />
    </div>
  )
}

interface SearchBarProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  onSubmit: (e: FormEvent) => void
  suggestions: Suggestion[]
  showSuggestions: boolean
  onSelectSuggestion: (word: string) => void
  onCloseSuggestions: () => void
}

function DesktopSearchBar({
  searchQuery,
  onSearchChange,
  onSubmit,
  suggestions,
  showSuggestions,
  onSelectSuggestion,
  onCloseSuggestions,
}: SearchBarProps) {
  return (
    <form
      onSubmit={onSubmit}
      className='flex-1 hidden max-w-xl mx-auto md:flex'>
      <SearchInput
        value={searchQuery}
        onChange={onSearchChange}
        autoFocus={false}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        onSelectSuggestion={onSelectSuggestion}
        onCloseSuggestions={onCloseSuggestions}
      />
    </form>
  )
}

function MobileSearchToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className='p-2 transition-all duration-300 rounded-lg md:hidden hover:bg-slate-800'>
      <Search className='w-5 h-5 text-slate-400' />
    </button>
  )
}

interface MobileSearchBarProps extends SearchBarProps {
  isExpanded: boolean
}

function MobileSearchBar({
  isExpanded,
  searchQuery,
  onSearchChange,
  onSubmit,
  suggestions,
  showSuggestions,
  onSelectSuggestion,
  onCloseSuggestions,
}: MobileSearchBarProps) {
  if (!isExpanded) return null

  return (
    <div className='px-4 pb-4 md:hidden'>
      <form onSubmit={onSubmit}>
        <SearchInput
          value={searchQuery}
          onChange={onSearchChange}
          autoFocus={true}
          suggestions={suggestions}
          showSuggestions={showSuggestions}
          onSelectSuggestion={onSelectSuggestion}
          onCloseSuggestions={onCloseSuggestions}
        />
      </form>
    </div>
  )
}

function PrimaryActionButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className='items-center hidden h-10 gap-2 px-4 font-semibold transition-all duration-300 rounded-lg sm:flex bg-amber-500 hover:bg-amber-600 text-slate-900'>
      ضيف كلمة
    </button>
  )
}

interface AuthSectionProps {
  user?: User
  onSignIn?: () => void
  onLogout?: () => void
}

function AuthSection({ user, onSignIn, onLogout }: AuthSectionProps) {
  if (user) {
    return <UserMenu user={user} onLogout={onLogout} />
  }

  return (
    <button
      onClick={onSignIn}
      className='h-10 px-4 font-semibold transition-all duration-300 border rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700'>
      دخول
    </button>
  )
}

function MainContent({ children }: { children: ReactNode }) {
  return <main className='pt-16'>{children}</main>
}
